import { CURRENCIES } from "../i18n/currencies";
import { usePrefs } from "../contexts/PrefsContext";
import { CircleDollarSign } from "lucide-react";

/**
 * Reusable currency picker for any financial page (Billing, Budgets, Expenses,
 * Quotes, Invoices, Project costs…). Changing it writes through PrefsContext
 * so the choice is mirrored to localStorage AND persisted to the user account
 * via PUT /api/auth/me/preferences — consistent across devices.
 *
 * Pass `value` + `onChange` to use as a controlled component without touching
 * the global preference (handy for per-invoice overrides where we don't want
 * to mutate the user's default).
 */
export default function CurrencyPicker({
    value, onChange, compact = false, testid = "currency-picker", label,
}) {
    const { prefs, updatePrefs } = usePrefs();
    const controlled = value !== undefined;
    const current = controlled ? value : prefs.currency;
    const handle = (code) => {
        if (controlled) onChange?.(code);
        else updatePrefs({ currency: code });
    };
    return (
        <label className={`inline-flex items-center gap-2 ${compact ? "text-xs" : "text-sm"}`} data-testid={testid}>
            {!compact && <CircleDollarSign size={14} className="text-[var(--text-ink)]/55" aria-hidden />}
            {label && <span className="text-[var(--text-ink)]/70">{label}</span>}
            <select value={current} onChange={(e) => handle(e.target.value)}
                    aria-label="Currency"
                    data-testid={`${testid}-select`}
                    className={`appearance-none bg-transparent border border-[rgba(26,19,24,0.15)] rounded-full ${compact ? "py-1 pl-2 pr-6" : "py-1.5 pl-3 pr-7"} hover:border-[var(--brand-purple)]/50 transition cursor-pointer`}>
                {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.code} {c.symbol}</option>
                ))}
            </select>
        </label>
    );
}
